import { useId, useState, type ReactNode } from 'react';
import { Minus, Plus } from 'lucide-react';

/**
 * C15: one accordion style site-wide (FAQ, quick links, first-visit steps).
 * Light grey rows, pink 20px title, round plus/minus, 300ms grid-rows reveal,
 * real <button aria-expanded>. Content is always in the DOM (SEO/no-JS).
 *
 *   <Accordion items={[{ id: 'first-visit', title: 'Your first visit', content: <p>...</p> }]} defaultOpen={['first-visit']} />
 */
export interface AccordionItem {
  id: string;
  title: ReactNode;
  content: ReactNode;
}

export interface AccordionProps {
  items: AccordionItem[];
  /** Ids open on first render. */
  defaultOpen?: string[];
  /** Let several rows stay open at once (default: one at a time). */
  allowMultiple?: boolean;
  /** Heading level wrapping each trigger (for the document outline). */
  headingLevel?: 2 | 3 | 4;
  className?: string;
}

export default function Accordion({
  items,
  defaultOpen = [],
  allowMultiple = false,
  headingLevel = 3,
  className = '',
}: AccordionProps) {
  const [open, setOpen] = useState<Set<string>>(() => new Set(defaultOpen));
  const baseId = useId();
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4';

  const toggle = (id: string) => {
    setOpen((prev) => {
      const next = new Set(allowMultiple ? prev : []);
      if (prev.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <ul className={`ve-acc ${className}`.trim()}>
      {items.map((item) => {
        const isOpen = open.has(item.id);
        const panelId = `${baseId}-${item.id}-panel`;
        const buttonId = `${baseId}-${item.id}-button`;
        return (
          <li key={item.id} className={`ve-acc-item${isOpen ? ' is-open' : ''}`}>
            <Heading style={{ margin: 0, fontSize: 'inherit', fontWeight: 'inherit' }}>
              <button
                type="button"
                id={buttonId}
                className="ve-acc-trigger"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => toggle(item.id)}
              >
                <span>{item.title}</span>
                <span className="ve-acc-icon" aria-hidden="true">
                  {isOpen ? <Minus /> : <Plus />}
                </span>
              </button>
            </Heading>
            <div id={panelId} role="region" aria-labelledby={buttonId} className="ve-acc-panel">
              <div className="ve-acc-panel-inner">
                <div className="ve-acc-body">{item.content}</div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
